/* PREPAID RENEWALS -- THE SCREENS' ONE PLANNER (prepaid renewals part 2, 2026-10-29; Brian's 10-29 decisions;
 * diffs/prepaid-renews-p2-build.txt). Both hosts call it -- the Step-6 wizard (staged rows, applyPrepaidPlanLocal) and the
 * Vendor Dashboard's Prepaid tab (stored rows, executePrepaidPlan) -- so the two screens cannot answer the same question two
 * ways. Every RULE stays in engines/prepaidAmort.js (the generator, the reconcile, Renews-off, the suggestion); this file only
 * finds the chain a row belongs to, turns an operator action into a PLAN, and applies the plan.
 *
 * REFS: a row's identity is prepaidRef(row) -- 'id:<id>' for a stored (GET) row, else its client_key ('id:<id>' on a wizard row
 * read back from the database, '<seed ref>#<start>' on a generated row, 'k:<collision key>' on a parsed row given a key by
 * ensurePrepaidRefs). Its PARENT is renewal_of_key (wizard) or 'id:' + renewalOfId (GET).
 *
 * A PLAN: { setAnswer: { ref, renews } | null, update: [{ ref, row }], create: [row], remove: [ref],
 *           relink: [{ ref, parentRef }], flagged: [{ ref, relinkTo }], collision, refused }.
 * Pure: no I/O, except executePrepaidPlan, which performs a plan through the calls it is handed. */
import {
  suggestPrepaidRenews, reconcilePrepaidRenewalChain, planPrepaidRenewsOff, prepaidRef, prepaidContractTerms,
  prepaidRenewRefusal, prepaidCollisionKey,
} from '../engines/prepaidAmort';
import { compareNamesNaturally } from '../engines/payrollKeys';

const CHAIN_GUARD = 1200;

export function prepaidParentRef(c) {
  if (!c) return null;
  if (c.renewal_of_key != null) return String(c.renewal_of_key);
  if (c.renewalOfId != null) return 'id:' + c.renewalOfId;
  if (c.renewal_of_id != null) return 'id:' + c.renewal_of_id;
  return null;
}

/* Why the screen offers no Renews answer for a row ("—"), or null. The generator refuses an event booking, an indefinite
 * contract and a zero amount (prepaidRenewRefusal); a one-month contract is a screen rule (Brian 10-29: "event bookings /
 * one-month contracts show —") -- a monthly prepaid series is the modal's Recurring series. */
export function prepaidRenewsDash(c) {
  const r = prepaidRenewRefusal(c);
  if (r === 'event-booking') return 'an event booking';
  if (r === 'no-length') return 'an indefinite contract';
  if (r === 'zero-amount') return 'a zero amount';
  if (r) return 'no start month';
  if (!(prepaidContractTerms(c).length_months > 1)) return 'a one-month contract';
  return null;
}

export function prepaidRowLabel(c) {
  const t = prepaidContractTerms(c);
  return (t.vendor_name || '(no vendor)') + ' ' + (t.start_month || '');
}

/* One entry per contract, in input order: { ref, answer, dash, suggest, reason, parentRef, parentLabel }. The suggestion is the
 * engine's (suggestPrepaidRenews) over the whole list; the screen shows it only while the row is unanswered. */
export function prepaidRenewsView(contracts, opts) {
  const list = contracts || [];
  const sug = suggestPrepaidRenews(list, { cutoff: (opts || {}).cutoff || null });
  const byRef = new Map();
  list.forEach(c => { const r = prepaidRef(c); if (r) byRef.set(r, c); });
  return list.map((c, i) => {
    const parentRef = prepaidParentRef(c);
    const parent = parentRef ? byRef.get(parentRef) : null;
    return {
      ref: prepaidRef(c), answer: prepaidContractTerms(c).renews, dash: prepaidRenewsDash(c),
      suggest: sug[i].suggest, reason: sug[i].reason, parentRef, parentLabel: parent ? prepaidRowLabel(parent) : null,
    };
  });
}

/* Give every row a ref (a parsed wizard row has none): 'k:' + the database's own collision key, which is unique among valid
 * rows; a repeat gets '#2', '#3'. Rows that already have a ref are returned as the same objects. */
export function ensurePrepaidRefs(rows) {
  const seen = new Set();
  (rows || []).forEach(r => { const k = prepaidRef(r); if (k) seen.add(k); });
  return (rows || []).map(r => {
    if (prepaidRef(r)) return r;
    let k = 'k:' + prepaidCollisionKey(r), n = 1;
    while (seen.has(n === 1 ? k : k + '#' + n)) n++;
    const key = n === 1 ? k : k + '#' + n;
    seen.add(key);
    return { ...r, client_key: key };
  });
}

function childrenOf(list) {
  const kids = new Map();
  for (const c of list) {
    const p = prepaidParentRef(c);
    if (p == null) continue;
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(c);
  }
  for (const a of kids.values()) a.sort((x, y) => String(prepaidContractTerms(x).start_month).localeCompare(String(prepaidContractTerms(y).start_month)));
  return kids;
}

/* The row's successors in chain order (each one's predecessor is the one before it). One child per row; a second child (which no
 * screen produces) is ignored -- the earliest start wins. */
export function prepaidDescendants(contracts, ref) {
  const list = contracts || [];
  const kids = childrenOf(list);
  const out = [], seen = new Set([ref]);
  let cur = ref;
  for (let k = 0; k < CHAIN_GUARD; k++) {
    const next = (kids.get(cur) || []).find(c => !seen.has(prepaidRef(c)));
    if (!next) break;
    out.push(next);
    cur = prepaidRef(next);
    seen.add(cur);
  }
  return out;
}

/* The first contract of the chain `ref` belongs to (the row itself when it has no parent in the list). */
export function prepaidChainRoot(contracts, ref) {
  const byRef = new Map();
  (contracts || []).forEach(c => { const r = prepaidRef(c); if (r) byRef.set(r, c); });
  let cur = ref;
  const seen = new Set([ref]);
  for (let k = 0; k < CHAIN_GUARD; k++) {
    const row = byRef.get(cur);
    const p = row ? prepaidParentRef(row) : null;
    if (p == null || !byRef.has(p) || seen.has(p)) break;
    cur = p;
    seen.add(cur);
  }
  return cur;
}

/* THE PREPAID LISTS' DISPLAY ORDER (Brian 10-29) -- both hosts (Step 6's Prepaid list, the Vendor Dashboard's Prepaid tab):
 * contracts ALPHABETICALLY by vendor (case-insensitive, natural -- compareNamesNaturally), within a vendor by start month, and
 * each chain's renewals DIRECTLY AFTER its first contract, in date order. Returns the INDICES of `contracts` in that order;
 * display only -- the rows, their stored sort_order, the import payload's order and the engine are untouched. A row whose
 * parent is not in the list heads its own chain; a cycle (no screen makes one) is broken where it is first met. */
export function prepaidDisplayOrder(contracts) {
  const list = contracts || [];
  const idxByRef = new Map();
  list.forEach((c, i) => { const r = prepaidRef(c); if (r != null && !idxByRef.has(r)) idxByRef.set(r, i); });
  const rootOf = i => {
    let cur = i;
    const seen = new Set([i]);
    for (let k = 0; k < CHAIN_GUARD; k++) {
      const p = prepaidParentRef(list[cur]);
      const pi = p == null ? undefined : idxByRef.get(p);
      if (pi === undefined || seen.has(pi)) break;
      cur = pi;
      seen.add(cur);
    }
    return cur;
  };
  const startOf = i => String(prepaidContractTerms(list[i]).start_month || '');
  const byStart = (a, b) => startOf(a).localeCompare(startOf(b)) || a - b;
  const chains = new Map();
  list.forEach((c, i) => { const r = rootOf(i); if (!chains.has(r)) chains.set(r, []); if (r !== i) chains.get(r).push(i); });
  const roots = [...chains.keys()].sort((a, b) =>
    compareNamesNaturally(prepaidContractTerms(list[a]).vendor_name, prepaidContractTerms(list[b]).vendor_name) || byStart(a, b));
  const out = [];
  for (const r of roots) { out.push(r); out.push(...chains.get(r).sort(byStart)); }
  return out;
}

const emptyPlan = () => ({ setAnswer: null, update: [], create: [], remove: [], relink: [], flagged: [], collision: null, refused: null });
const fromReconcile = (plan, rec) => {
  plan.update = rec.update; plan.create = rec.create; plan.remove = rec.remove;
  plan.collision = rec.collision; plan.refused = rec.refused;
  return plan;
};

/* THE OPERATOR ANSWERS Renews on `ref`. Yes: the row renews and its chain is (re)built through forecastThrough by the engine's
 * reconcile (a fresh row: its successors generated; a row with kept successors: they are kept and the chain continues after
 * them; a collision stops it, reported). No (D3): planPrepaidRenewsOff -- every unedited successor removed, every edited one KEPT
 * and FLAGGED for the operator (relinked to its nearest kept ancestor until they choose). `before` = the row's image before an
 * edit made in the same save (the modal), so its successors are judged against what generated them (D4). */
export function planPrepaidRenewsAnswer(contracts, ref, answer, opts) {
  const o = opts || {};
  const list = contracts || [];
  const row = list.find(c => prepaidRef(c) === ref);
  const plan = emptyPlan();
  if (!row) { plan.refused = 'no-row'; return plan; }
  plan.setAnswer = { ref, renews: answer === true ? true : answer === false ? false : null };
  const desc = prepaidDescendants(list, ref);
  if (answer === true) {
    const rec = reconcilePrepaidRenewalChain({
      root: { ...row, renews: true }, descendants: desc, before: o.before ? { [ref]: o.before } : {},
      forecastThrough: o.forecastThrough, existing: list,
    });
    return fromReconcile(plan, rec);
  }
  if (answer === false) {
    const off = planPrepaidRenewsOff({ row: o.before || row, descendants: desc });
    plan.remove = off.remove;
    plan.flagged = off.flagged;
    plan.relink = off.flagged.filter(f => f.relinkTo).map(f => ({ ref: f.ref, parentRef: f.relinkTo }));
  }
  return plan;
}

/* A ROW IN A CHAIN WAS EDITED (D4). `contracts` is the list AFTER the edit; `before` the edited row's image before it. The chain
 * is reconciled from its root: the rows after the edit regenerate FROM it; an edit elsewhere in the chain is kept. A chain whose
 * root does not renew has nothing to reconcile (an empty plan) -- every contract no one answered Yes on, on every tenant. */
export function planPrepaidEdit(contracts, ref, before, opts) {
  const o = opts || {};
  const list = contracts || [];
  const rootRef = prepaidChainRoot(list, ref);
  const root = list.find(c => prepaidRef(c) === rootRef);
  const plan = emptyPlan();
  if (!root || prepaidContractTerms(root).renews !== true) return plan;
  const rec = reconcilePrepaidRenewalChain({
    root, descendants: prepaidDescendants(list, rootRef), before: before ? { [ref]: before } : {},
    forecastThrough: o.forecastThrough, existing: list,
  });
  return fromReconcile(plan, rec);
}

/* The operator's choice on a FLAGGED row (an edited renewal kept when Renews was turned off): keep it as a stand-alone contract
 * (no parent, Renews not answered -- the suggestion shows, and P28 names it if it is the vendor's last), or delete it. */
export function planPrepaidFlaggedKeep(ref) {
  const plan = emptyPlan();
  plan.setAnswer = { ref, renews: null };
  plan.relink = [{ ref, parentRef: null }];
  return plan;
}
export function planPrepaidFlaggedDelete(ref) {
  const plan = emptyPlan();
  plan.remove = [ref];
  return plan;
}

/* THE WIZARD: apply a plan to the staged rows. `adapt(row, prev)` turns a generated / reconciled row into the host's row shape
 * (AdminPage passes step6PrepaidFromModal, the same fold the modal's Apply uses). Returns the new rows array. */
export function applyPrepaidPlanLocal(rows, plan, adapt) {
  const fold = adapt || ((row, prev) => ({ ...(prev || {}), ...row }));
  let out = (rows || []).slice();
  const at = ref => out.findIndex(r => prepaidRef(r) === ref);
  if (plan.setAnswer) { const i = at(plan.setAnswer.ref); if (i >= 0) out[i] = { ...out[i], renews: plan.setAnswer.renews }; }
  for (const u of plan.update || []) { const i = at(u.ref); if (i >= 0) out[i] = fold(u.row, out[i]); }
  for (const l of plan.relink || []) { const i = at(l.ref); if (i >= 0) out[i] = { ...out[i], renewal_of_key: l.parentRef }; }
  const gone = new Set(plan.remove || []);
  out = out.filter(r => !gone.has(prepaidRef(r)));
  for (const row of plan.create || []) out.push(fold(row, null));
  return out;
}

/* THE DASHBOARD: perform a plan through the API, in order -- the answer (PUT), the updates (PUT), the creates (sequential POSTs,
 * each renewal_of_key resolved to its predecessor's NEW id), the removals (DELETE), the relinks (PUT). Every PUT carries the row's
 * FULL terms (the route assigns each term column); renews / renewal_of_id travel only where the plan sets them.
 * api = { put(id, body), post(body) -> the created row, del(id) }. A 409 'prepaid_duplicate' on a POST stops the chain there (D5)
 * and is returned as `duplicate`; any other failure throws. Returns { done: [ 'PUT id' | 'POST <client_key> -> id' | ... ], duplicate }. */
export async function executePrepaidPlan(plan, contracts, api) {
  const list = contracts || [];
  const byRef = new Map(), idOf = new Map();
  list.forEach(c => { const r = prepaidRef(c); if (r) { byRef.set(r, c); if (c.id != null) idOf.set(r, c.id); } });
  const resolve = key => {
    if (key == null) return null;
    if (idOf.has(key)) return idOf.get(key);
    const m = /^id:(\d+)$/.exec(String(key));
    return m ? Number(m[1]) : null;
  };
  const termsBody = src => {
    const t = prepaidContractTerms(src);
    return {
      vendor_name: t.vendor_name, vendor_id: t.vendor_id, account: t.account, is_line: t.is_line, start_month: t.start_month,
      length_months: t.length_months, prepaid_amount: t.prepaid_amount, amort: t.amort, event_identifier: t.event_identifier,
      event_month: t.event_month, amort_lag_months: t.amort_lag_months,
    };
  };
  const done = [];
  let duplicate = null;
  const idFor = ref => { const id = resolve(ref); if (id == null) throw new Error('prepaid plan: no stored id for ' + ref); return id; };
  if (plan.setAnswer) {
    const c = byRef.get(plan.setAnswer.ref), id = idFor(plan.setAnswer.ref);
    await api.put(id, { ...termsBody(c), renews: plan.setAnswer.renews });
    done.push('PUT ' + id + ' renews=' + plan.setAnswer.renews);
  }
  for (const u of plan.update || []) {
    const id = idFor(u.ref);
    await api.put(id, { ...termsBody(u.row), renews: true, renewal_of_id: resolve(u.row.renewal_of_key) });
    done.push('PUT ' + id + ' (regenerated)');
  }
  for (const row of plan.create || []) {
    try {
      const created = await api.post({ ...termsBody(row), renews: true, renewal_of_id: resolve(row.renewal_of_key) });
      if (created && created.id != null) idOf.set(String(row.client_key), created.id);
      done.push('POST ' + row.client_key + ' -> ' + (created && created.id));
    } catch (err) {
      const data = err && err.response && err.response.data;
      if (data && data.error === 'prepaid_duplicate') { duplicate = { row, message: data.message || null }; break; }
      throw err;
    }
  }
  for (const ref of plan.remove || []) {
    const id = idFor(ref);
    await api.del(id);
    done.push('DELETE ' + id);
  }
  for (const l of plan.relink || []) {
    const c = byRef.get(l.ref), id = idFor(l.ref);
    await api.put(id, { ...termsBody(c), renewal_of_id: resolve(l.parentRef) });
    done.push('PUT ' + id + ' renewal_of_id=' + resolve(l.parentRef));
  }
  return { done, duplicate };
}

/* Plain-English notices, shared by both hosts. */
export function prepaidCollisionText(collision, contracts) {
  if (!collision) return null;
  const hit = (contracts || []).find(c => prepaidRef(c) === collision.with);
  const t = hit ? prepaidContractTerms(hit) : null;
  return 'The renewal starting ' + collision.start_month + ' was not created: ' +
    (t ? t.vendor_name + ' ' + t.start_month + ' (' + t.length_months + ' months, the same amount) already exists' : 'a contract with the same terms already exists') +
    ' -- the renewals stop there.';
}
export function prepaidDuplicateText(dup) {
  if (!dup) return null;
  const t = prepaidContractTerms(dup.row);
  return 'The renewal ' + t.vendor_name + ' ' + t.start_month + ' was not created: the same contract already exists -- the renewals stop there.';
}
